import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ActionBar, Button, Dialog, Modal, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { useEnpsStore } from '../store'

/**
 * @fileoverview eNPS Viewers Tab
 *
 * Manage viewers who can view results for this specific eNPS configuration
 */

interface EnpsViewer {
	id: string
	enpsConfigId: string
	userId: string
	firstName: string
	lastName: string
	email: string
	externalId: string
	active: boolean
}

interface User {
	id: string
	organizationId: string
	email: string
	firstName: string
	lastName: string
	externalId: string
	active: boolean
}

type EnpsViewerSearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

type UserSearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

export function EnpsViewersTab() {
	const { organizationId } = useParams<{ organizationId: string; enpsId: string }>()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])
	const [selectedViewersToDelete, setSelectedViewersToDelete] = useState<EnpsViewer[]>([])
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const { enpsConfig } = useEnpsStore()

	const addEnpsViewersMutation = useMutation({
		...trpc.admin.enps.addViewers.mutationOptions(),
		onSuccess: () => {
			showToast('User(s) added as viewer(s)')
			setIsModalOpen(false)
			setSelectedUsers([])
		},
		onError: () => {
			showToast('Something went wrong, the user(s) could not be added as viewer(s)', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.enps.searchViewers.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const deleteEnpsViewersMutation = useMutation({
		...trpc.admin.enps.deleteViewers.mutationOptions(),
		onSuccess: () => {
			showToast('Viewer(s) removed')
			setSelectedViewersToDelete([])
			setIsDeleteDialogOpen(false)
		},
		onError: () => {
			showToast('Something went wrong, the viewer(s) could not be removed', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.enps.searchViewers.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleOpenModal = () => {
		setIsModalOpen(true)
	}

	useEffect(() => {
		if (isModalOpen) {
			queryClient.invalidateQueries({
				queryKey: [['enps', 'searchPossibleViewers']],
				exact: false,
				refetchType: 'all',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isModalOpen, queryClient.invalidateQueries])

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedUsers([])
	}

	const handleAddSelected = () => {
		if (!enpsConfig || !organizationId) return
		addEnpsViewersMutation.mutate({
			enpsConfigId: enpsConfig.id,
			organizationId,
			userIds: selectedUsers.map((user) => user.id),
		})
	}

	const handleDeleteSelected = () => {
		if (!enpsConfig || !organizationId) return
		deleteEnpsViewersMutation.mutate({
			enpsConfigId: enpsConfig.id,
			organizationId,
			userIds: selectedViewersToDelete.map((viewer) => viewer.userId),
		})
	}

	const handleOpenDeleteDialog = () => {
		setIsDeleteDialogOpen(true)
	}

	const handleCloseDeleteDialog = () => {
		setIsDeleteDialogOpen(false)
	}

	return (
		<div className='mt-6'>
			<div className='px-6 max-w-6xl mb-4 flex justify-between items-center'>
				<Text variant='small' className='text-gray-600'>
					Viewers can access and view results for this specific eNPS configuration. Add users who should have read-only
					access to survey results.
				</Text>
				<div className='flex gap-2'>
					<Button variant='primary' onClick={handleOpenModal}>
						Add Viewer
					</Button>
					<Button
						variant='destructive'
						onClick={handleOpenDeleteDialog}
						disabled={selectedViewersToDelete.length === 0}>
						Remove Selected
					</Button>
				</div>
			</div>

			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<EnpsViewer, EnpsViewerSearchInput>
					queryOptionsGenerator={(options) => ({
						...trpc.admin.enps.searchViewers.queryOptions({
							...options,
							organizationId: organizationId as string,
							enpsConfigId: enpsConfig?.id || '',
						}),
						enabled: !!enpsConfig?.id,
					})}
					selectable
					onSelectionChange={setSelectedViewersToDelete}
					columns={[
						{
							field: 'firstName',
							label: 'Name',
							colSpan: 2,
							render: (viewer) => (
								<Text variant='xsmall' weight='regular'>
									{`${viewer.firstName} ${viewer.lastName}`}
								</Text>
							),
						},
						{
							field: 'externalId',
							label: 'ID',
							colSpan: 1,
						},
						{
							field: 'email',
							label: 'Email',
							colSpan: 2,
						},
					]}
					emptyState={{
						title: 'No viewers found',
						description: 'Add users who should have read-only access to survey results',
						actionLabel: 'Add Viewer',
						onAction: handleOpenModal,
					}}
				/>
			</div>

			{/* Add Viewers Modal */}
			<Modal isOpen={isModalOpen} onClose={handleCloseModal} title='Add Survey Viewers'>
				<div className='flex flex-col gap-4'>
					<Text variant='small' className='text-gray-600'>
						Select users to add as viewers for this survey. Viewers can access and view survey results but cannot make
						changes.
					</Text>

					<GenericTable<User, UserSearchInput>
						queryOptionsGenerator={(options) => ({
							...trpc.admin.enps.searchPossibleViewers.queryOptions({
								...options,
								organizationId: organizationId as string,
								enpsConfigId: enpsConfig?.id || '',
							}),
							enabled: !!enpsConfig?.id,
						})}
						selectable
						onSelectionChange={setSelectedUsers}
						columns={[
							{
								field: 'firstName',
								label: 'Name',
								colSpan: 2,
								render: (user) => (
									<Text variant='xsmall' weight='regular'>
										{`${user.firstName} ${user.lastName}`}
									</Text>
								),
							},
							{
								field: 'externalId',
								label: 'ID',
								colSpan: 1,
							},
							{
								field: 'email',
								label: 'Email',
								colSpan: 2,
							},
						]}
						emptyState={{
							title: 'No users available to add',
							description: 'All eligible users are already viewers',
						}}
					/>

					<ActionBar
						variant='modal'
						rightActions={[
							{
								label: 'Cancel',
								onClick: handleCloseModal,
								variant: 'secondary',
							},
							{
								label: `Add ${selectedUsers.length} Viewer${selectedUsers.length !== 1 ? 's' : ''}`,
								onClick: handleAddSelected,
								variant: 'primary',
								disabled: selectedUsers.length === 0 || addEnpsViewersMutation.isPending,
							},
						]}
					/>
				</div>
			</Modal>

			{/* Delete Confirmation Dialog */}
			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteSelected}
				title='Remove Viewers'
				message={`Are you sure you want to remove ${selectedViewersToDelete.length} viewer${selectedViewersToDelete.length !== 1 ? 's' : ''}? They will lose access to view survey results.`}
				type='delete'
				confirmLabel='Remove'
				isConfirmLoading={deleteEnpsViewersMutation.isPending}
			/>
		</div>
	)
}
